// eslint-disable-next-line import/no-extraneous-dependencies
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';

import { paths } from 'src/routes/paths';

import { jwtDecode } from 'src/utils/isValidToken';

import { AxiosHttpClient } from 'src/http';
import { useAuthStore } from 'src/auth/auth.store';
import { useAnalytics } from 'src/stores/analytics/analytics.store';
import { API_URL, NODE_ENV, TEST_AB_EVENT_ID } from 'src/config-global';
import { useCustomContent } from 'src/stores/custom-content/custom-content.store';
import { AnalyticsRepository } from 'src/repositories/analytics/analytics.repository';

import { useBoolean } from './use-boolean';
import { useResponsive } from './use-responsive';

export const useApp = () => {
    const api = new AxiosHttpClient(API_URL);
    const repository = new AnalyticsRepository(api);
    const isMobile = useResponsive('down', 'md')

    const { pathname } = window.location;
    const segments = pathname.split('/');

    const buURL = segments[1];
    const eventURL = segments[2];

    const accessToken = useAuthStore((state) => state.accessToken)

    const { eventData, businessUnity, getCustomContent } = useCustomContent((state) => state)
    const { analyticsDeviceId, setAnalyticsDeviceId } = useAnalytics((state) => state)
    const isLoading = useBoolean(true);
    const navigate = useNavigate();
    const location = useLocation();

    const getData = async () => {
        try {
            if (!!buURL && !!eventURL) {
                await getCustomContent(buURL, eventURL)
            } else if (buURL === 'wifi') {
                navigate('wifi')
            }
        } catch (error) {
            navigate(paths.error.notFound)
        } finally {
            isLoading.onFalse()
        }
    }

    useEffect(() => {
        getData()
    }, [])

    useEffect(() => {
        const sendLPAccessLog = async () => {
            const eventResult = await repository.sendInstantEvent([{
                eventType: 'action',
                eventDate: new Date(),
                actionType: 'action',
                parameter: 'Landing Page access',
                screenName: location.pathname,
                otherData: {
                    deviceId: analyticsDeviceId || null,
                    event_date: new Date().toLocaleString('pt-BR', {
                        timeZone: 'America/Sao_Paulo',
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                    }),
                    log_name: 'landing_page_access',
                    category: "landing_page_access",
                    bu_id: businessUnity?.id,
                    bu: businessUnity?.name,
                    lp_event: eventData?.name,
                    event_id: eventData?._id,
                    deviceType: isMobile ? 'mobile' : 'desktop',
                    lpVersion: accessToken && jwtDecode(accessToken)?.data?.rdSummitVersion && eventData?._id === TEST_AB_EVENT_ID ?
                        jwtDecode(accessToken)?.data?.rdSummitVersion : undefined
                },
                target: ['lp_analytics', 'roxcode'],
                origin: 'LPEvents'
            }]);

            if (!analyticsDeviceId && eventResult && eventResult?.value?.deviceId) {
                setAnalyticsDeviceId(eventResult.value.deviceId);
            }

            const logData = {
                message: ' LP Events landing page access',
                tag: 'lp-events-landing-page-access',
                env: NODE_ENV === "production" ? "production" : "sandbox",
                level: "info",
                data: {
                    eventId: eventData?._id,
                    BUId: businessUnity?.id,
                }
            }
            await repository.sendLog([logData]);
        };

        if (eventData) {
            sendLPAccessLog();
        }
    }, [eventData]);

    return {
        isLoading
    };
};
